import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from './PortalLayout';
import { 
  Map, 
  Layers, 
  Filter, 
  RefreshCw, 
  Loader2,
  MapPin,
  Calendar,
  User,
  FileText,
  ChevronDown,
  X,
  Flame,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Load Google Maps Script with MarkerClusterer
// Wait for Google Maps API (loaded in index.html)
const loadGoogleMapsScript = (callback) => {
  if (window.google && window.google.maps) {
    if (callback) callback();
    return;
  }
  // API still loading from index.html, poll until ready
  const checkReady = setInterval(() => {
    if (window.google && window.google.maps) {
      clearInterval(checkReady);
      if (callback) callback();
    }
  }, 100);
};

const AdminJobsMap = ({ embedded = false }) => {
  const { token } = useAuth();
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const markerClustererRef = useRef(null);
  const heatmapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const jobsForClickRef = useRef([]);
  const selectedJobRef = useRef(null);
  const hiddenStatusesRef = useRef(new Set());
  
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [geocodingStatus, setGeocodingStatus] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // View mode
  const [viewMode, setViewMode] = useState('markers'); // 'markers' or 'heatmap'
  
  // Selected job for info panel
  const [selectedJob, setSelectedJob] = useState(null);

  // Instant client-side status filter (statuses hidden from the map)
  const [hiddenStatuses, setHiddenStatuses] = useState(new Set());

  // "Not on map" (no-address) panel
  const [showUnmapped, setShowUnmapped] = useState(false);
  const [unmappedJobs, setUnmappedJobs] = useState([]);
  const [unmappedLoading, setUnmappedLoading] = useState(false);

  // Keep hidden-status set in a ref so the canvas overlay (bound once) can read
  // it, and redraw instantly when a status chip is toggled (no server refetch).
  useEffect(() => {
    hiddenStatusesRef.current = hiddenStatuses;
    if (viewMode === 'markers' && markerClustererRef.current && markerClustererRef.current.draw) {
      markerClustererRef.current.draw();
    }
  }, [hiddenStatuses, viewMode]);

  // Keep a ref of the selected job so the (once-bound) map click listener can
  // toggle it closed when the same pin is clicked again.
  useEffect(() => {
    selectedJobRef.current = selectedJob;
  }, [selectedJob]);

  // Initialize Google Maps
  useEffect(() => {
    loadGoogleMapsScript(() => {
      setMapReady(true);
    });
  }, []);

  // Initialize map once ready
  useEffect(() => {
    if (mapReady && mapRef.current && !googleMapRef.current) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: { lat: 33.5779, lng: -101.8552 }, // Lubbock, TX as default center
        zoom: 10,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
          { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
          { featureType: "poi", stylers: [{ visibility: "off" }] },
          { featureType: "transit", stylers: [{ visibility: "off" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
          { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
          { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
          { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
          { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f2835" }] },
          { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#f3d19c" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
          { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#515c6d" }] }
        ]
      });
      
      googleMapRef.current = map;
      infoWindowRef.current = new window.google.maps.InfoWindow();
      
      // Expose map for debugging
      window.__adminMap = map;
      
      // Map click listener for job selection
      googleMapRef.current.addListener('click', (e) => {
        const clickLat = e.latLng.lat();
        const clickLng = e.latLng.lng();
        const currentJobs = jobsForClickRef.current;
        
        if (!currentJobs.length) return;
        
        // Get current zoom to calculate click threshold
        const zoom = googleMapRef.current.getZoom();
        // At zoom 5, threshold ~2 degrees; at zoom 15, threshold ~0.001 degrees
        const threshold = 360 / Math.pow(2, zoom) * 0.05;
        
        let closestJob = null;
        let closestDist = Infinity;
        
        for (const job of currentJobs) {
          if (hiddenStatusesRef.current && hiddenStatusesRef.current.has(job.status || 'Unknown')) continue;
          const lat = parseFloat(job.lat);
          const lng = parseFloat(job.lng);
          if (isNaN(lat) || isNaN(lng)) continue;
          
          const dist = Math.sqrt(Math.pow(lat - clickLat, 2) + Math.pow(lng - clickLng, 2));
          if (dist < closestDist) {
            closestDist = dist;
            closestJob = job;
          }
        }
        
        if (closestJob && closestDist < threshold) {
          const currentSel = selectedJobRef.current;
          if (currentSel && currentSel.id === closestJob.id) {
            // Clicking the same pin again closes the box
            setSelectedJob(null);
          } else {
            setSelectedJob(closestJob);
            googleMapRef.current.panTo({ lat: parseFloat(closestJob.lat), lng: parseFloat(closestJob.lng) });
          }
        }
      });
      
      // Load initial data
      fetchStats();
      fetchClients();
      fetchJobs();
    }
  }, [mapReady]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/map/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
      setGeocodingStatus(data.geocoding_status);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch clients for filter
  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/map/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setClients(data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (clientFilter) params.append('client_email', clientFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await fetch(`${API_URL}/api/admin/map/jobs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setJobs(data.points || []);
      jobsForClickRef.current = data.points || [];
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, clientFilter, dateFrom, dateTo]);

  // Update map markers/heatmap when jobs change
  useEffect(() => {
    const map = googleMapRef.current;
    if (!map || !jobs.length) return;
    
    // Validate map is a real Google Maps instance
    if (typeof map.getZoom !== 'function') return;
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap && marker.setMap(null));
    markersRef.current = [];
    
    // Clear existing overlay
    if (markerClustererRef.current) {
      try {
        if (markerClustererRef.current.setMap) {
          markerClustererRef.current.setMap(null);
        }
      } catch (e) { /* ignore cleanup errors */ }
      markerClustererRef.current = null;
    }
    
    // Clear existing heatmap
    if (heatmapRef.current) {
      try { heatmapRef.current.setMap(null); } catch (e) { /* ignore */ }
      heatmapRef.current = null;
    }
    
    try {
    if (viewMode === 'heatmap') {
      // Create heatmap
      const heatmapData = jobs
        .filter(job => !isNaN(parseFloat(job.lat)) && !isNaN(parseFloat(job.lng)))
        .map(job => ({
          location: new window.google.maps.LatLng(parseFloat(job.lat), parseFloat(job.lng)),
          weight: 1
        }));
      
      heatmapRef.current = new window.google.maps.visualization.HeatmapLayer({
        data: heatmapData,
        map: map,
        radius: 30,
        opacity: 0.7
      });
    } else {
      // Use Canvas Overlay for smooth performance with 10,000+ points
      const statusColors = {
        'Served': '#10B981',
        'Attempted': '#3B82F6',
        'Active': '#3B82F6',
        'Non-Service': '#F59E0B',
        'Non-Served': '#F59E0B',
        'Canceled': '#EF4444',
        'Cancelled': '#EF4444',
        'default': '#6B7280'
      };
      
      // Create custom canvas overlay
      class CanvasOverlay extends window.google.maps.OverlayView {
        constructor(jobs) {
          super();
          this.jobs = jobs;
          this.canvas = null;
        }
        
        onAdd() {
          this.canvas = document.createElement('canvas');
          this.canvas.style.position = 'absolute';
          this.canvas.style.pointerEvents = 'none';
          
          const panes = this.getPanes();
          panes.overlayLayer.appendChild(this.canvas);
        }
        
        draw() {
          const projection = this.getProjection();
          if (!projection || !this.canvas) return;
          
          const map = this.getMap();
          const bounds = map.getBounds();
          if (!bounds) return;
          
          const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
          const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
          
          const width = ne.x - sw.x;
          const height = sw.y - ne.y;
          
          this.canvas.style.left = sw.x + 'px';
          this.canvas.style.top = ne.y + 'px';
          this.canvas.width = width;
          this.canvas.height = height;
          
          const ctx = this.canvas.getContext('2d');
          ctx.clearRect(0, 0, width, height);
          
          // Draw all points — dot size scales with zoom so pins don't swallow
          // whole states when zoomed out.
          const zoom = map.getZoom() || 10;
          const dotSize = zoom <= 5 ? 2 : zoom <= 7 ? 2.5 : zoom <= 9 ? 3.5 : zoom <= 12 ? 5 : 6.5;
          const hidden = hiddenStatusesRef.current || new Set();
          
          for (const job of this.jobs) {
            if (hidden.has(job.status || 'Unknown')) continue;
            const lat = parseFloat(job.lat);
            const lng = parseFloat(job.lng);
            if (isNaN(lat) || isNaN(lng)) continue;
            
            const pos = projection.fromLatLngToDivPixel(
              new window.google.maps.LatLng(lat, lng)
            );
            
            if (pos) {
              const x = pos.x - sw.x;
              const y = pos.y - ne.y;
              
              // Only draw if in bounds (with small buffer)
              if (x >= -dotSize && x <= width + dotSize && y >= -dotSize && y <= height + dotSize) {
                const color = statusColors[job.status] || statusColors['default'];
                
                ctx.beginPath();
                ctx.arc(x, y, dotSize, 0, 2 * Math.PI);
                ctx.fillStyle = color;
                ctx.fill();
                ctx.strokeStyle = 'rgba(255,255,255,0.5)';
                ctx.lineWidth = 1;
                ctx.stroke();
              }
            }
          }
        }
        
        onRemove() {
          if (this.canvas && this.canvas.parentNode) {
            this.canvas.parentNode.removeChild(this.canvas);
            this.canvas = null;
          }
        }
      }
      
      // Create and add the overlay
      const overlay = new CanvasOverlay(jobs);
      overlay.setMap(map);
      markerClustererRef.current = overlay;
    }
    } catch (e) {
      console.error('Error creating map overlay:', e);
    }
    
    // Fit bounds to show all points
    if (jobs.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      jobs.forEach(job => {
        const lat = parseFloat(job.lat);
        const lng = parseFloat(job.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          bounds.extend({ lat, lng });
        }
      });
      googleMapRef.current.fitBounds(bounds);
    } else if (jobs.length === 1) {
      googleMapRef.current.setCenter({ lat: parseFloat(jobs[0].lat), lng: parseFloat(jobs[0].lng) });
      googleMapRef.current.setZoom(14);
    }
  }, [jobs, viewMode]);

  // Start geocoding (mode: 'pending' | 'failed')
  const startGeocoding = async (mode = 'pending') => {
    try {
      const response = await fetch(`${API_URL}/api/admin/map/geocode/start?mode=${mode}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      alert(data.message);
      
      // Start polling for status
      pollGeocodingStatus();
    } catch (error) {
      console.error('Error starting geocoding:', error);
    }
  };

  // Poll geocoding status
  const pollGeocodingStatus = async () => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/map/geocode/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setGeocodingStatus(data);
        
        if (data.status === 'running') {
          setTimeout(checkStatus, 2000); // Check every 2 seconds
        } else {
          // Any terminal state (complete/not_started) -> refresh counts + pins
          fetchStats();
          fetchJobs();
        }
      } catch (error) {
        console.error('Error checking geocoding status:', error);
        // Network hiccup shouldn't hang the button forever — refresh once
        fetchStats();
      }
    };
    
    checkStatus();
  };

  // Apply filters
  const applyFilters = () => {
    fetchJobs();
    setShowFilters(false);
  };

  // Clear filters
  const clearFilters = () => {
    setStatusFilter('');
    setClientFilter('');
    setDateFrom('');
    setDateTo('');
    setTimeout(fetchJobs, 100);
  };

  // Open the "Not on map" panel and load the list
  const openUnmapped = async () => {
    setShowUnmapped(true);
    setUnmappedLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/map/jobs/unmapped`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setUnmappedJobs(data.jobs || []);
    } catch (e) {
      console.error('Error loading unmapped jobs:', e);
    }
    setUnmappedLoading(false);
  };

  // Instant status filter chips (client-side). Hex matches CanvasOverlay pin
  // colors; inline styles avoid the global admin CSS overriding bg-* classes.
  const STATUS_META = [
    { key: 'Served', hex: '#10B981' },
    { key: 'Attempted', hex: '#3B82F6' },
    { key: 'Non-Service', hex: '#F59E0B' },
    { key: 'Unknown', hex: '#6B7280' }
  ];
  const statusCounts = jobs.reduce((acc, j) => {
    const s = j.status || 'Unknown';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const toggleStatus = (key) => {
    setHiddenStatuses(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <Wrapper embedded={embedded}>
      <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="admin-jobs-map">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Map className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-semibold text-gray-900">Job Locations Map</h1>
            {stats && stats.geocoded !== undefined && (
              <span className="text-sm text-gray-500">
                {(stats.geocoded || 0).toLocaleString()} of {(stats.total_jobs || 0).toLocaleString()} jobs mapped
              </span>
            )}
            {stats && stats.unmapped > 0 && (
              <button
                onClick={openUnmapped}
                data-testid="unmapped-badge"
                title="Jobs that have no physical address (social media, text, email or phone) and can't be plotted"
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-300 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-colors"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {stats.unmapped.toLocaleString()} not on map
              </button>
            )}
          </div>
        
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('markers')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'markers' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <MapPin className="w-4 h-4 inline mr-1" />
              Markers
            </button>
            <button
              onClick={() => setViewMode('heatmap')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'heatmap' 
                  ? 'bg-white text-orange-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Flame className="w-4 h-4 inline mr-1" />
              Heat Map
            </button>
          </div>
          
          {/* Filter Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              showFilters || statusFilter || clientFilter || dateFrom || dateTo
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {(statusFilter || clientFilter || dateFrom || dateTo) && (
              <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                {[statusFilter, clientFilter, dateFrom, dateTo].filter(Boolean).length}
              </span>
            )}
          </button>
          
          {/* Geocode Button */}
          {stats && stats.pending > 0 && (
            <button
              onClick={() => startGeocoding('pending')}
              data-testid="start-geocoding-btn"
              disabled={geocodingStatus?.status === 'running'}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {geocodingStatus?.status === 'running' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {geocodingStatus.mode === 'failed' ? 'Retrying' : 'Geocoding'}... ({(geocodingStatus.processed || 0).toLocaleString()}/{(geocodingStatus.total || 0).toLocaleString()})
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Start Geocoding ({stats.pending.toLocaleString()} pending)
                </>
              )}
            </button>
          )}

          {/* Retry Failed Button */}
          {stats && stats.failed > 0 && geocodingStatus?.status !== 'running' && (
            <button
              onClick={() => startGeocoding('failed')}
              data-testid="retry-failed-geocoding-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              title="Re-attempt jobs that previously failed to geocode"
            >
              <RefreshCw className="w-4 h-4" />
              Retry Failed ({stats.failed.toLocaleString()})
            </button>
          )}
        </div>
      </div>
      
      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-gray-50 border-b px-6 py-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Served">Served</option>
                <option value="Attempted">Attempted</option>
                <option value="Non-Service">Non-Service</option>
              </select>
            </div>
            
            {/* Client Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 max-w-[200px]"
              >
                <option value="">All Clients</option>
                {clients.map(client => (
                  <option key={client.email} value={client.email}>
                    {client.email} ({client.job_count})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            {/* Action Buttons */}
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Apply Filters
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
            >
              Clear
            </button>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="flex-1 flex relative">
        {/* Map */}
        <div ref={mapRef} className="flex-1" style={{ minHeight: '500px' }} />
        
        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-lg">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-gray-700">Loading map data...</span>
            </div>
          </div>
        )}
        
        {/* Job Detail Panel */}
        {selectedJob && (
          <div className="absolute right-4 top-16 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden" data-testid="job-detail-panel">
            <div className="bg-gray-100 px-4 py-3 flex items-center justify-between border-b border-gray-200">
              <h3 className="font-bold text-gray-900">Job #{selectedJob.job_number}</h3>
              <button 
                onClick={() => setSelectedJob(null)}
                data-testid="close-job-detail-btn"
                aria-label="Close details"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-900 text-white hover:bg-red-600 transition-colors shadow-sm"
              >
                <X className="w-4 h-4" strokeWidth={3} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-gray-900">{selectedJob.recipient}</p>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recipient</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedJob.address}</p>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Service Address</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Layers className="w-4 h-4 text-gray-500 mt-0.5" />
                <div>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                    selectedJob.status === 'Served' ? 'bg-green-100 text-green-800' :
                    selectedJob.status === 'Active' ? 'bg-blue-100 text-blue-800' :
                    selectedJob.status === 'Non-Served' ? 'bg-amber-100 text-amber-800' :
                    selectedJob.status === 'Canceled' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedJob.status}
                  </span>
                </div>
              </div>
              
              {selectedJob.case_title && (
                <div className="flex items-start gap-3">
                  <FileText className="w-4 h-4 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{selectedJob.case_title}</p>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Case Title</p>
                  </div>
                </div>
              )}
              
              {selectedJob.server_name && (
                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-gray-900" data-testid="job-detail-server-name">{selectedJob.server_name}</p>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Server</p>
                  </div>
                </div>
              )}
              
              {selectedJob.served_at && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(selectedJob.served_at).toLocaleDateString()}
                    </p>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Date Served</p>
                  </div>
                </div>
              )}
              
              {selectedJob.date && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(selectedJob.date).toLocaleDateString()}
                    </p>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Received Date</p>
                  </div>
                </div>
              )}
              
              <div className="pt-2 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-600">{selectedJob.client}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Status Filter Legend (click to show/hide instantly) */}
        {viewMode === 'markers' && jobs.length > 0 && (
          <div className="absolute left-4 bottom-4 bg-white rounded-lg shadow-lg border p-3" data-testid="status-filter-legend">
            <p className="text-xs font-semibold text-gray-700 mb-2">Filter by status</p>
            <div className="space-y-1.5">
              {STATUS_META.filter(m => statusCounts[m.key] > 0).map(m => {
                const isHidden = hiddenStatuses.has(m.key);
                return (
                  <button
                    key={m.key}
                    onClick={() => toggleStatus(m.key)}
                    data-testid={`status-chip-${m.key.toLowerCase().replace(/[^a-z]/g,'-')}`}
                    className={`flex items-center gap-2 w-full text-left px-1 py-0.5 rounded transition-opacity ${isHidden ? 'opacity-35' : 'opacity-100'} hover:bg-gray-50`}
                  >
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: m.hex }}></span>
                    <span className={`text-xs flex-1 ${isHidden ? 'line-through text-gray-400' : 'text-gray-700'}`}>{m.key}</span>
                    <span className="text-xs font-semibold text-gray-500">{(statusCounts[m.key] || 0).toLocaleString()}</span>
                  </button>
                );
              })}
            </div>
            {hiddenStatuses.size > 0 && (
              <button
                onClick={() => setHiddenStatuses(new Set())}
                data-testid="show-all-statuses-btn"
                className="mt-2 text-[11px] font-medium text-blue-600 hover:text-blue-800"
              >
                Show all
              </button>
            )}
          </div>
        )}
        
        {/* "Not on map" side panel */}
        {showUnmapped && (
          <div className="absolute inset-y-0 right-0 w-96 max-w-full bg-white shadow-2xl border-l border-gray-200 z-20 flex flex-col" data-testid="unmapped-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-amber-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <h3 className="font-bold text-gray-900">Not on map ({unmappedJobs.length.toLocaleString()})</h3>
              </div>
              <button
                onClick={() => setShowUnmapped(false)}
                data-testid="close-unmapped-btn"
                aria-label="Close"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-900 text-white hover:bg-red-600 transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={3} />
              </button>
            </div>
            <p className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
              These jobs have no physical address (social media, text, email or phone) so they can't be plotted.
            </p>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {unmappedLoading ? (
                <div className="p-4 flex items-center gap-2 text-gray-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : unmappedJobs.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">Nothing here — every job is on the map.</div>
              ) : (
                unmappedJobs.map(j => (
                  <div key={j.id} className="p-3 hover:bg-gray-50" data-testid="unmapped-job-row">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-bold text-gray-900">Job #{j.job_number}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">{j.reason}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-700 mt-0.5">{j.recipient}</p>
                    <p className="text-xs text-gray-400 mt-0.5 break-words">{j.address}</p>
                    {j.client && <p className="text-[11px] text-gray-400 mt-0.5">{j.client}</p>}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {/* Stats Bar - positioned bottom right to avoid overlap */}
        <div className="absolute right-4 bottom-4 bg-white rounded-lg shadow-lg border px-4 py-2">
          <p className="text-sm font-medium text-gray-900">
            {jobs.length.toLocaleString()} jobs displayed
          </p>
        </div>
      </div>
    </div>
    </Wrapper>
  );
};

const Wrapper = ({ embedded, children }) =>
  embedded ? <>{children}</> : <PortalLayout>{children}</PortalLayout>;

export default AdminJobsMap;
